import { useCanViewEmails } from '../../../helpers/useCanViewEmails';
import React from 'react';
import { Tooltip } from '@mui/material';
import cx from 'classnames';

import { ACCESS_TYPE, ACCESS_TYPE_ENUM } from '../../../enums/ACCESS_ENUM';
import { FILE_ACCESS_TYPE, FOLDER_TYPE } from '../../../enums/TYPES';
import FolderIcon from '../../../assets/icons/folder-open.svg';
import { ReactComponent as UsersIcon } from '../../../assets/icons/CollaboratorsUsersIcon.svg';
import StorageIcon from '../../../assets/icons/storage.svg';
import { DateFormatter } from '../../../helpers/dateFormatter';
import { bytesToGB, bytesToMB, secondsToHours } from '../../../helpers';
import { convertSeconds } from '../../Timeline/utils';
import ShareButton from '../ShareButton';
import styles from './FolderContentRow.module.scss';
import { SortOptions } from '../../../enums/SORT';
import { getDateColumnTooltipLabel, getDateColumnTooltipValue, getDateColumnValue } from '../DateSortHeader';

type FolderContentRowVariant = 'mine' | 'shared' | 'all-content' | 'collaborative';

type FolderContentRowProps = {
  folder: FOLDER_TYPE & { highest_user_access?: FILE_ACCESS_TYPE };
  variant: FolderContentRowVariant;
  onOpen: () => void;
  onShare?: () => void;
  showOwnerColumn?: boolean;
  dateSortOption?: SortOptions | null;
};

const getAccessLabel = (access?: FILE_ACCESS_TYPE) => {
  const accessTitle = access?.access?.title as ACCESS_TYPE_ENUM | undefined;

  if (!accessTitle) {
    return 'Subfolder';
  }

  return ACCESS_TYPE[accessTitle] || 'Subfolder';
};

const FolderContentRow: React.FC<FolderContentRowProps> = ({
  folder,
  variant,
  onOpen,
  onShare,
  showOwnerColumn = false,
  dateSortOption,
}) => {
  const canViewEmails = useCanViewEmails();
  const isSharedView = variant === 'shared';
  const isAllContentView = variant === 'all-content';
  const isDefaultFolderView = variant === 'mine' || variant === 'collaborative';
  const ownerName = folder.owner?.username || (canViewEmails ? folder.owner?.email : 'User') || '';
  const size = Number(folder.size || 0);
  const collaboratorIds = new Set<string>();
  (folder.collaborators || []).forEach((user) => {
    if (user?.id) collaboratorIds.add(String(user.id));
  });
  (folder.group_collaborators || []).forEach((group) => {
    (group?.analysts || []).forEach((user) => {
      if (user?.id) collaboratorIds.add(String(user.id));
    });
  });
  if (folder.owner?.id) collaboratorIds.delete(String(folder.owner.id));
  const collaboratorCount = collaboratorIds.size;

  return (
    <div
      className={cx(
        styles.row,
        isAllContentView && styles.allContentRow,
      )}
      onClick={onOpen}
    >
      <div className={styles.left}>
        <img
          src={folder.thumbnail_url || FolderIcon}
          alt="folder"
          className={cx(styles.icon, folder.thumbnail_url && styles.customIcon)}
        />
        <div className={styles.name}>{folder.name}</div>
      </div>

      {showOwnerColumn && !isSharedView && (
        <div className={cx(styles.cell, styles.ownerWide)}>{ownerName || 'Owner'}</div>
      )}

      {isSharedView && <div className={cx(styles.cell, styles.ownerWide)}>{ownerName}</div>}

      {isDefaultFolderView && (
        <Tooltip title={collaboratorCount ? `${collaboratorCount} collaborator${collaboratorCount === 1 ? '' : 's'}` : 'No collaborators'}>
          <div className={cx(styles.cell, styles.collaboratorCell)}>
            <UsersIcon className={styles.collaboratorIcon} />
            {collaboratorCount}
          </div>
        </Tooltip>
      )}
      {isAllContentView && (
        <div className={cx(styles.cell, styles.statusCell)}>Subfolder</div>
      )}
      {(isSharedView || isAllContentView) && (
        <div className={cx(styles.cell, styles.accessCell)}>
          {getAccessLabel(folder.highest_user_access)}
        </div>
      )}

      <Tooltip title={`${getDateColumnTooltipLabel(dateSortOption)}: ${DateFormatter(getDateColumnTooltipValue(folder, dateSortOption), true)}`}>
        <div className={cx(styles.cell, styles.dateWide)}>{DateFormatter(getDateColumnValue(folder, dateSortOption), true)}</div>
      </Tooltip>

      <div className={cx(styles.cell, styles.sizeCell)}>
        <img src={StorageIcon} alt="storage" className={styles.storageIcon} />
        {bytesToGB(size) > 1 ? `${bytesToGB(size).toFixed(2)} GB` : `${bytesToMB(size).toFixed(0)} MB`}
      </div>

      {!isSharedView && (
        <Tooltip title={folder.duration ? convertSeconds(folder.duration, false, true) : ''}>
          <div className={cx(styles.cell, styles.durationCell)}>
            {folder.duration ? secondsToHours(folder.duration) : '0h'}
          </div>
        </Tooltip>
      )}

      <div className={styles.actionCell} onClick={(event) => event.stopPropagation()}>
        {onShare ? (
          <button
            type="button"
            onClick={onShare}
            className={styles.actionIconButton}
            aria-label="Share folder"
          >
            <ShareButton />
          </button>
        ) : (
          <span className={styles.actionPlaceholder} />
        )}
      </div>
    </div>
  );
};

export default FolderContentRow;
